import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Nav, Tab } from "react-bootstrap";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import MasterLayout from "../masterLayout/MasterLayout";
import {
  delete_tiktok_comment,
  get_tiktok_account,
  get_tiktok_auth_url,
  get_tiktok_comments,
  get_tiktok_creator_info,
  get_tiktok_insights,
  get_tiktok_publish_status,
  get_tiktok_videos,
  hide_tiktok_comment,
  publish_tiktok_photo,
  publish_tiktok_video,
  reply_tiktok_comment,
  save_tiktok_account,
  server_post_data,
  tiktok_oauth_callback,
  upload_public_media,
} from "../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import "../components/CSS/gmb-premium.css";
import "../components/CSS/facebook-premium.css";
import "../components/CSS/tiktok-premium.css";

/* ---------------------------------------------------------------------------
   Static config
--------------------------------------------------------------------------- */

const SOCIAL_PROFILES = [
  { key: "google", label: "Google Business", icon: "mdi:google", ready: false },
  { key: "facebook", label: "Facebook", icon: "mdi:facebook", ready: false },
  { key: "instagram", label: "Instagram", icon: "mdi:instagram", ready: false },
  { key: "tiktok", label: "TikTok", icon: "ic:baseline-tiktok", ready: true },
  { key: "twitter", label: "Twitter", icon: "ri:twitter-x-fill", ready: false },
  { key: "linkedin", label: "LinkedIn", icon: "mdi:linkedin", ready: false },
];

const CHANNEL_TABS = [
  { key: "tiktok", label: "TikTok", ready: true },
  { key: "instagram", label: "Instagram", ready: false },
  { key: "facebook", label: "Facebook", ready: false },
  { key: "linkedin", label: "LinkedIn", ready: false },
  { key: "twitter", label: "Twitter", ready: false },
  { key: "google", label: "Google", ready: false },
];

const POST_TYPES = [
  { key: "VIDEO", label: "Video", icon: "mdi:video-outline" },
  { key: "PHOTO", label: "Photo Carousel", icon: "mdi:image-multiple-outline" },
  { key: "DRAFT", label: "Send to Drafts", icon: "mdi:tray-arrow-down" },
];

// TikTok returns raw enum names; these are the labels their own app uses.
const PRIVACY_LABELS = {
  PUBLIC_TO_EVERYONE: {
    label: "Everyone",
    hint: "Anyone on TikTok can see this post",
    icon: "mdi:earth",
  },
  MUTUAL_FOLLOW_FRIENDS: {
    label: "Friends",
    hint: "Only people who follow each other",
    icon: "mdi:account-multiple-outline",
  },
  FOLLOWER_OF_CREATOR: {
    label: "Followers",
    hint: "Only your followers can see this post",
    icon: "mdi:account-heart-outline",
  },
  SELF_ONLY: {
    label: "Only Me",
    hint: "Private — visible only to you",
    icon: "mdi:lock-outline",
  },
};

const TIPS = [
  {
    title: "Hook In Three Seconds",
    body: "TikTok decides how far to push a video from its earliest watch time, so lead with the payoff, not a build-up.",
  },
  {
    title: "Vertical, Always",
    body: "Shoot 9:16 at 1080x1920. Anything letterboxed gets noticeably less reach than full-screen footage.",
  },
  {
    title: "Caption For Search",
    body: "Captions are indexed now — write what someone would actually type to find your video, not just hashtags.",
  },
];

const emptyPost = {
  post_type: "VIDEO",
  title: "",
  description: "",
  privacy_level: "SELF_ONLY",
  disable_comment: false,
  disable_duet: false,
  disable_stitch: false,
  brand_content_toggle: false,
  brand_organic_toggle: false,
};

/* ------------------------------------------------------------------------ */

function TikTokPanel() {
  const [loading, setLoading] = useState(false);
  const [loadingNote, setLoadingNote] = useState("");
  const [activeTab, setActiveTab] = useState("account");

  // account
  const [account, setAccount] = useState(null);
  const [creatorInfo, setCreatorInfo] = useState(null);

  // videos
  const [videos, setVideos] = useState([]);

  // composer
  const [showComposer, setShowComposer] = useState(false);
  const [form, setForm] = useState(emptyPost);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState("");
  const [photos, setPhotos] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [channel, setChannel] = useState("tiktok");

  // publish status
  const [publishState, setPublishState] = useState(null);
  const pollRef = useRef(null);

  // detail / comments
  const [showDetail, setShowDetail] = useState(false);
  const [detailVideo, setDetailVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsBlocked, setCommentsBlocked] = useState(false);
  const [replyBox, setReplyBox] = useState({});

  // insights
  const [insights, setInsights] = useState(null);

  const clickRef = useRef(0);

  /* ------------------------- common caller ------------------------- */
  const call = useCallback(
    async (url, fields = {}, files = {}, silent = false) => {
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") fd.append(k, v);
      });
      Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));

      try {
        const res = await server_post_data(url, fd);
        const parts = res.message.split("~@~");
        if (parseInt(parts[0]) === 1) {
          if (!silent) handleError(parts[1]);
          return null;
        }
        const parsed = JSON.parse(parts[1]);
        if (parsed.error) {
          if (!silent) handleError(parsed.message || "TikTok API error");
          return { _error: parsed };
        }
        return parsed;
      } catch (e) {
        if (!silent) handleError("network");
        return null;
      }
    },
    [],
  );

  /* ------------------------- loaders ------------------------- */
  const loadAccount = useCallback(async () => {
    setLoading(true);
    const d = await call(get_tiktok_account);
    if (d?.account) setAccount(d.account);
    setLoading(false);
  }, [call]);

  const loadCreatorInfo = useCallback(async () => {
    const d = await call(get_tiktok_creator_info);
    if (d?.creatorInfo) {
      setCreatorInfo(d.creatorInfo);

      // Default to the least-exposing option TikTok actually allows, rather
      // than assuming public is available.
      const options = d.creatorInfo.privacyLevelOptions || [];
      setForm((s) => ({
        ...s,
        privacy_level: options.includes("SELF_ONLY")
          ? "SELF_ONLY"
          : options[0] || "SELF_ONLY",
        disable_comment: d.creatorInfo.commentDisabled || s.disable_comment,
        disable_duet: d.creatorInfo.duetDisabled || s.disable_duet,
        disable_stitch: d.creatorInfo.stitchDisabled || s.disable_stitch,
      }));
    }
    return d?.creatorInfo || null;
  }, [call]);

  const loadVideos = useCallback(async () => {
    setLoading(true);
    const d = await call(get_tiktok_videos, { limit: 20 });
    setVideos(d?.videos || []);
    setLoading(false);
  }, [call]);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    const d = await call(get_tiktok_insights, { limit: 20 });
    setInsights(d && !d._error ? d : null);
    setLoading(false);
  }, [call]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    if (activeTab === "videos") loadVideos();
    if (activeTab === "insights") loadInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Stop polling if the component unmounts mid-publish
  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  /* ------------------------- OAuth popup flow ------------------------- */
  const exchangeCode = useCallback(
    async (code, state) => {
      setLoading(true);
      const d = await call(tiktok_oauth_callback, { code, state });
      setLoading(false);
      if (!d?.account) return;

      setLoading(true);
      const saved = await call(save_tiktok_account, {
        open_id: d.account.openId,
        username: d.account.username,
        access_token: d.accessToken,
        refresh_token: d.refreshToken,
        expires_in: d.expiresIn,
      });
      setLoading(false);

      if (saved) {
        handleSuccess(`Connected as @${d.account.username}`);
        loadAccount();
      }
    },
    [call, loadAccount],
  );

  // TikTok has no browser SDK — the popup posts the auth code back here.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.source !== "tiktok-oauth") return;

      if (data.error) {
        handleError(data.error);
        return;
      }
      if (data.code) exchangeCode(data.code, data.state);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [exchangeCode]);

  const handleConnect = async () => {
    const d = await call(get_tiktok_auth_url);
    if (!d?.authUrl) return;

    const w = 600;
    const h = 760;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;

    const popup = window.open(
      d.authUrl,
      "tiktok-oauth",
      `width=${w},height=${h},left=${left},top=${top}`,
    );
    if (!popup) handleError("Please allow popups to connect TikTok");
  };

  /* ------------------------- composer ------------------------- */
  const resetComposer = () => {
    setForm(emptyPost);
    setVideoFile(null);
    setVideoPreview("");
    setPhotos([]);
    setCoverIndex(0);
    setPublishState(null);
    setChannel("tiktok");
  };

  const openComposer = async () => {
    resetComposer();
    setLoading(true);
    await loadCreatorInfo();
    setLoading(false);
    setShowComposer(true);
  };

  const onPickVideo = (file) => {
    if (!file) return;

    const maxSec = creatorInfo?.maxVideoPostDurationSec;
    const url = URL.createObjectURL(file);

    // Check duration client-side so the creator finds out now, not after a
    // multi-minute upload that TikTok then rejects.
    const probe = document.createElement("video");
    probe.preload = "metadata";
    probe.onloadedmetadata = () => {
      if (maxSec && probe.duration > maxSec) {
        handleError(
          `This account can post videos up to ${maxSec} seconds — your video is ${Math.round(
            probe.duration,
          )} seconds`,
        );
        URL.revokeObjectURL(url);
        return;
      }
      setVideoFile(file);
      setVideoPreview(url);
    };
    probe.onerror = () => {
      setVideoFile(file);
      setVideoPreview(url);
    };
    probe.src = url;
  };

  const onPickPhoto = async (file) => {
    if (!file) return;
    if (photos.length >= 35) {
      handleError("A photo carousel can hold at most 35 images");
      return;
    }

    setLoadingNote("Uploading image...");
    setLoading(true);
    const d = await call(upload_public_media, {}, { media_file: file });
    setLoading(false);
    setLoadingNote("");

    if (!d?.url) return;
    setPhotos((s) => [
      ...s,
      { url: d.url, preview: URL.createObjectURL(file) },
    ]);
  };

  /* ------------------------- publish + status polling ------------------------- */
  const pollStatus = useCallback(
    async (publishId, attempt = 0) => {
      const d = await call(
        get_tiktok_publish_status,
        { publish_id: publishId },
        {},
        true,
      );

      if (!d || d._error) {
        setPublishState({
          status: "FAILED",
          message: d?._error?.message || "Could not check the publish status",
          isFailed: true,
        });
        return;
      }

      setPublishState(d);

      if (d.isComplete) {
        handleSuccess(d.message);
        loadVideos();
        return;
      }
      if (d.isFailed) {
        handleError(d.message);
        return;
      }

      // TikTok can take a while on longer videos; give up after ~2 minutes
      // and tell the user to check the app rather than spinning forever.
      if (attempt >= 30) {
        setPublishState({
          status: "PROCESSING",
          message:
            "TikTok is still processing this post — check the TikTok app in a few minutes.",
        });
        return;
      }

      pollRef.current = setTimeout(
        () => pollStatus(publishId, attempt + 1),
        4000,
      );
    },
    [call, loadVideos],
  );

  const publish = async () => {
    const t = form.post_type;

    if (t === "PHOTO") {
      if (photos.length === 0) {
        handleError("Please add at least one image");
        return;
      }
      if (form.title.length > 90) {
        handleError("Title cannot exceed 90 characters");
        return;
      }
    } else {
      if (!videoFile) {
        handleError("Please select a video first");
        return;
      }
      if (t === "VIDEO" && form.description.length > 2200) {
        handleError("Caption cannot exceed 2200 characters");
        return;
      }
    }

    // TikTok rejects a post that declares itself branded content while set
    // to a private audience, so it's caught here with a clearer message.
    if (form.brand_content_toggle && form.privacy_level === "SELF_ONLY") {
      handleError(
        "Branded content cannot be posted with the Only Me privacy setting",
      );
      return;
    }

    if (clickRef.current === 1) return;
    clickRef.current = 1;
    setLoadingNote("Uploading to TikTok...");
    setLoading(true);
    setPublishState(null);

    let d;
    if (t === "PHOTO") {
      d = await call(publish_tiktok_photo, {
        photo_urls: JSON.stringify(photos.map((p) => p.url)),
        title: form.title,
        description: form.description,
        cover_index: coverIndex,
        privacy_level: form.privacy_level,
        disable_comment: form.disable_comment ? "1" : "0",
        brand_content_toggle: form.brand_content_toggle ? "1" : "0",
        brand_organic_toggle: form.brand_organic_toggle ? "1" : "0",
      });
    } else {
      d = await call(
        publish_tiktok_video,
        {
          publish_target: t === "DRAFT" ? "inbox" : "direct",
          title: form.description,
          privacy_level: form.privacy_level,
          disable_comment: form.disable_comment ? "1" : "0",
          disable_duet: form.disable_duet ? "1" : "0",
          disable_stitch: form.disable_stitch ? "1" : "0",
          brand_content_toggle: form.brand_content_toggle ? "1" : "0",
          brand_organic_toggle: form.brand_organic_toggle ? "1" : "0",
        },
        { media_file: videoFile },
      );
    }

    setLoading(false);
    setLoadingNote("");
    clickRef.current = 0;

    if (d && !d._error && d.publishId) {
      setPublishState({ status: "PROCESSING", message: d.message });
      pollStatus(d.publishId);
    }
  };

  /* ------------------------- detail / comments ------------------------- */
  const openDetail = async (v) => {
    setDetailVideo(v);
    setShowDetail(true);
    setComments([]);
    setCommentsBlocked(false);
    setLoading(true);

    const d = await call(
      get_tiktok_comments,
      { video_id: v.videoId, limit: 40 },
      {},
      true,
    );

    if (d?._error) {
      // Comment scopes need TikTok audit approval; surface that inline
      // instead of firing an error toast every time a video is opened.
      setCommentsBlocked(true);
    } else {
      setComments(d?.comments || []);
    }
    setLoading(false);
  };

  const sendReply = async (commentId) => {
    const msg = (replyBox[commentId] || "").trim();
    if (!msg) return;
    if (msg.length > 150) {
      handleError("A TikTok comment cannot exceed 150 characters");
      return;
    }
    setLoading(true);
    const d = await call(reply_tiktok_comment, {
      video_id: detailVideo.videoId,
      comment_id: commentId,
      text: msg,
    });
    setLoading(false);
    if (d && !d._error) {
      handleSuccess("Reply posted successfully");
      setReplyBox((s) => ({ ...s, [commentId]: "" }));
      openDetail(detailVideo);
    }
  };

  const toggleHide = async (c) => {
    setLoading(true);
    const d = await call(hide_tiktok_comment, {
      comment_id: c.commentId,
      hide: c.status === "hidden" ? "0" : "1",
    });
    setLoading(false);
    if (d && !d._error) {
      handleSuccess(d.message || "Updated successfully");
      openDetail(detailVideo);
    }
  };

  const removeComment = async (commentId) => {
    setLoading(true);
    const d = await call(delete_tiktok_comment, { comment_id: commentId });
    setLoading(false);
    if (d && !d._error) {
      handleSuccess("Comment deleted successfully");
      openDetail(detailVideo);
    }
  };

  /* ------------------------- helpers ------------------------- */
  const initials = (n) => (n || "?").trim().charAt(0).toUpperCase();

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const compact = (n) => {
    if (n === undefined || n === null) return 0;
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n;
  };

  const fmtDuration = (sec) => {
    if (!sec) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const isPhoto = form.post_type === "PHOTO";
  const isDraft = form.post_type === "DRAFT";

  const privacyOptions = creatorInfo?.privacyLevelOptions || [];
  const canPostPublic = privacyOptions.some((p) => p !== "SELF_ONLY");

  return (
    <MasterLayout>
      <div className="gmb-premium fb-premium tt-premium">
        {loading && <Loader />}

        {/* ------------------------- Topbar ------------------------- */}
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5 className="gmb-display">TikTok</h5>
            <span className="gmb-royal-badge">
              <Icon icon="ic:baseline-tiktok" /> Social Suite
            </span>
            {account && (
              <span className="gmb-live">
                <span className="gmb-live-dot" /> Live
              </span>
            )}
          </div>
          <div className="gmb-topbar-actions">
            <button
              className="btn btn-outline-primary-600"
              onClick={handleConnect}
            >
              <Icon icon="mdi:account-switch-outline" /> Switch Account
            </button>
            <button className="btn btn-primary-600" onClick={openComposer}>
              <Icon icon="ic:baseline-plus" /> Create Post
            </button>
          </div>
        </div>

        {/* ------------------------- Account hero ------------------------- */}
        {account && (
          <div className="fb-page-hero">
            <img
              src={account.avatar || ""}
              alt={account.username}
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
            <div>
              <h6>
                {account.displayName}
                {account.isVerified && (
                  <Icon
                    icon="mdi:check-decagram"
                    style={{ color: "var(--tt-cyan)", marginLeft: 4 }}
                  />
                )}
              </h6>
              <span>@{account.username}</span>
            </div>
            <div className="fb-page-stats">
              <div>
                <b className="gmb-mono">{compact(account.followerCount)}</b>
                <small>Followers</small>
              </div>
              <div>
                <b className="gmb-mono">{compact(account.likesCount)}</b>
                <small>Likes</small>
              </div>
              <div>
                <b className="gmb-mono">{compact(account.videoCount)}</b>
                <small>Videos</small>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------- Tabs ------------------------- */}
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav className="nav-tabs">
            <Nav.Item>
              <Nav.Link eventKey="account">
                <Icon icon="mdi:information-outline" /> Profile
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="videos">
                <Icon icon="mdi:grid" /> Videos
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="insights">
                <Icon icon="mdi:chart-line" /> Analytics
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* ------------------ PROFILE ------------------ */}
            <Tab.Pane eventKey="account">
              {!account ? (
                <div className="gmb-empty">
                  No TikTok account is connected yet — use “Switch Account”
                  above to connect one.
                </div>
              ) : (
                <div className="gmb-card" style={{ padding: 20 }}>
                  <div className="gmb-detail-row">
                    <strong>Display Name</strong>
                    <span>{account.displayName}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Handle</strong>
                    <span>@{account.username}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Bio</strong>
                    <span>{account.bio || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Followers</strong>
                    <span>{compact(account.followerCount)}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Following</strong>
                    <span>{compact(account.followingCount)}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Total Likes</strong>
                    <span>{compact(account.likesCount)}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Videos</strong>
                    <span>{account.videoCount}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Profile</strong>
                    <span>
                      <a
                        href={account.profileUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {account.profileUrl}
                      </a>
                    </span>
                  </div>
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ VIDEOS ------------------ */}
            <Tab.Pane eventKey="videos">
              <div className="d-flex justify-content-end mb-3">
                <button
                  className="btn btn-outline-primary-600 btn-sm"
                  onClick={loadVideos}
                >
                  <Icon icon="mdi:refresh" /> Refresh
                </button>
              </div>

              {videos.length === 0 ? (
                <div className="gmb-empty">No videos published yet.</div>
              ) : (
                <div className="row g-3">
                  {videos.map((v) => (
                    <div
                      className="col-xl-2 col-lg-3 col-md-4 col-6"
                      key={v.videoId}
                    >
                      <div
                        className="tt-grid-tile"
                        onClick={() => openDetail(v)}
                      >
                        <img src={v.coverImage} alt={v.title} />
                        <span className="tt-grid-duration">
                          {fmtDuration(v.duration)}
                        </span>
                        <span className="tt-grid-views">
                          <Icon icon="mdi:play" /> {compact(v.viewCount)}
                        </span>
                        <div className="tt-grid-overlay">
                          <span>
                            <Icon icon="mdi:heart" /> {compact(v.likeCount)}
                          </span>
                          <span>
                            <Icon icon="mdi:comment" />{" "}
                            {compact(v.commentCount)}
                          </span>
                          <span>
                            <Icon icon="mdi:share" /> {compact(v.shareCount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ ANALYTICS ------------------ */}
            <Tab.Pane eventKey="insights">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="gmb-tab-sub mb-0">
                  TikTok reports metrics per video rather than by date, so this
                  covers your most recent posts.
                </div>
                <button
                  className="btn btn-outline-primary-600 btn-sm"
                  onClick={loadInsights}
                >
                  <Icon icon="mdi:refresh" /> Refresh
                </button>
              </div>

              {!insights ? (
                <div className="gmb-empty">No analytics loaded yet.</div>
              ) : (
                <>
                  <div className="row g-3 mb-4">
                    {[
                      ["Views", insights.totals.views, "mdi:play-outline"],
                      ["Likes", insights.totals.likes, "mdi:heart-outline"],
                      [
                        "Comments",
                        insights.totals.comments,
                        "mdi:comment-outline",
                      ],
                      ["Shares", insights.totals.shares, "mdi:share-outline"],
                      [
                        "Engagements",
                        insights.totals.engagements,
                        "mdi:gesture-tap",
                      ],
                      [
                        "Engagement Rate",
                        `${insights.totals.engagementRate}%`,
                        "mdi:chart-donut",
                      ],
                      [
                        "Average Views",
                        insights.totals.averageViews,
                        "mdi:chart-timeline-variant",
                      ],
                      [
                        "Videos Analysed",
                        insights.videosAnalysed,
                        "mdi:counter",
                      ],
                    ].map(([label, value, icon]) => (
                      <div className="col-xl-3 col-lg-4 col-md-6" key={label}>
                        <div className="gmb-card gmb-metric-card">
                          <div
                            className="gmb-metric-icon"
                            style={{
                              background: "#ffe4ec",
                              color: "var(--tt-pink)",
                            }}
                          >
                            <Icon icon={icon} />
                          </div>
                          <div className="gmb-metric-label">{label}</div>
                          <div className="gmb-metric-total gmb-mono">
                            {typeof value === "number" ? compact(value) : value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {insights.topVideos?.length > 0 && (
                    <div className="gmb-card" style={{ padding: 20 }}>
                      <div className="gmb-section-title mb-3">
                        Top Performing Videos
                      </div>
                      {insights.topVideos.map((v) => (
                        <div className="tt-top-row" key={v.videoId}>
                          <img
                            className="tt-top-thumb"
                            src={v.coverImage}
                            alt=""
                          />
                          <div className="tt-top-text">
                            {v.title || "(no caption)"}
                            {v.shareUrl && (
                              <>
                                {" "}
                                <a
                                  className="gmb-competitor-maps-link"
                                  href={v.shareUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View
                                </a>
                              </>
                            )}
                          </div>
                          <div className="tt-top-stat">
                            <b>{compact(v.views)}</b>
                            <small>{v.engagementRate}% rate</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* ==================== COMPOSER MODAL ==================== */}
        <Modal
          className="gmb-modal fb-composer-modal"
          size="xl"
          centered
          backdrop={publishState && !publishState.isComplete ? "static" : true}
          show={showComposer}
          onHide={() => setShowComposer(false)}
        >
          <Modal.Body>
            <div className="fb-composer">
              {/* ---------- LEFT: profiles + privacy ---------- */}
              <div className="fb-composer-col fb-composer-rail">
                <div className="fb-composer-heading">
                  Select Social Profiles
                </div>

                {SOCIAL_PROFILES.map((sp) => {
                  const connected = sp.key === "tiktok" && !!account;
                  return (
                    <div
                      key={sp.key}
                      className={`fb-social-item ${sp.key} ${
                        connected ? "is-connected" : ""
                      }`}
                    >
                      <div
                        className="fb-social-head"
                        style={
                          sp.key === "tiktok"
                            ? { background: "var(--gmb-royal)" }
                            : undefined
                        }
                      >
                        <Icon icon={sp.icon} />
                        {sp.label}
                        {connected && (
                          <span className="fb-social-count">(1)</span>
                        )}
                      </div>
                      <div
                        className="fb-social-foot"
                        style={
                          sp.key === "tiktok"
                            ? { background: "#ffe4ec", color: "#b3003a" }
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          disabled={!sp.ready}
                          onClick={sp.ready ? handleConnect : undefined}
                        >
                          {connected ? "Connected" : "Connect Account"}
                        </button>
                        <span className="fb-social-status">
                          <Icon
                            icon={
                              connected
                                ? "mdi:check-circle"
                                : "mdi:check-circle-outline"
                            }
                          />
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div className="fb-rail-divider" />

                <div className="fb-field-title">Who Can Watch</div>
                <div className="tt-privacy-list">
                  {privacyOptions.length === 0 ? (
                    <div className="gmb-char-count">
                      Loading available options...
                    </div>
                  ) : (
                    privacyOptions.map((p) => {
                      const meta = PRIVACY_LABELS[p] || {
                        label: p,
                        hint: "",
                        icon: "mdi:eye-outline",
                      };
                      const active = form.privacy_level === p;
                      return (
                        <button
                          key={p}
                          type="button"
                          className={`tt-privacy-option ${active ? "active" : ""}`}
                          onClick={() =>
                            setForm((s) => ({ ...s, privacy_level: p }))
                          }
                        >
                          <Icon icon={meta.icon} className="tt-privacy-icon" />
                          <span>
                            <b>{meta.label}</b>
                            <small>{meta.hint}</small>
                          </span>
                          {active && (
                            <Icon
                              icon="mdi:check-circle"
                              className="tt-privacy-check"
                            />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>

                <div className="fb-rail-divider" />

                <div className="fb-field-title">Interactions</div>
                <div className="tt-toggle-list">
                  <div
                    className={`tt-toggle-row ${
                      creatorInfo?.commentDisabled ? "disabled" : ""
                    }`}
                  >
                    <span className="tt-toggle-label">
                      Allow comments
                      {creatorInfo?.commentDisabled && (
                        <small>Turned off in your TikTok settings</small>
                      )}
                    </span>
                    <button
                      type="button"
                      disabled={creatorInfo?.commentDisabled}
                      className={`fb-switch ${
                        !form.disable_comment ? "on" : ""
                      }`}
                      onClick={() =>
                        setForm((s) => ({
                          ...s,
                          disable_comment: !s.disable_comment,
                        }))
                      }
                    />
                  </div>

                  {!isPhoto && (
                    <>
                      <div
                        className={`tt-toggle-row ${
                          creatorInfo?.duetDisabled ? "disabled" : ""
                        }`}
                      >
                        <span className="tt-toggle-label">
                          Allow Duet
                          {creatorInfo?.duetDisabled && (
                            <small>Not available for this account</small>
                          )}
                        </span>
                        <button
                          type="button"
                          disabled={creatorInfo?.duetDisabled}
                          className={`fb-switch ${!form.disable_duet ? "on" : ""}`}
                          onClick={() =>
                            setForm((s) => ({
                              ...s,
                              disable_duet: !s.disable_duet,
                            }))
                          }
                        />
                      </div>

                      <div
                        className={`tt-toggle-row ${
                          creatorInfo?.stitchDisabled ? "disabled" : ""
                        }`}
                      >
                        <span className="tt-toggle-label">
                          Allow Stitch
                          {creatorInfo?.stitchDisabled && (
                            <small>Not available for this account</small>
                          )}
                        </span>
                        <button
                          type="button"
                          disabled={creatorInfo?.stitchDisabled}
                          className={`fb-switch ${
                            !form.disable_stitch ? "on" : ""
                          }`}
                          onClick={() =>
                            setForm((s) => ({
                              ...s,
                              disable_stitch: !s.disable_stitch,
                            }))
                          }
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="fb-rail-divider" />

                <div className="fb-field-title">Content Disclosure</div>
                <div className="tt-toggle-list">
                  <div className="tt-toggle-row">
                    <span className="tt-toggle-label">
                      Promoting my own business
                      <small>Your own brand, product or service</small>
                    </span>
                    <button
                      type="button"
                      className={`fb-switch ${
                        form.brand_organic_toggle ? "on" : ""
                      }`}
                      onClick={() =>
                        setForm((s) => ({
                          ...s,
                          brand_organic_toggle: !s.brand_organic_toggle,
                        }))
                      }
                    />
                  </div>
                  <div className="tt-toggle-row">
                    <span className="tt-toggle-label">
                      Paid partnership
                      <small>Shows a "Paid partnership" label</small>
                    </span>
                    <button
                      type="button"
                      className={`fb-switch ${
                        form.brand_content_toggle ? "on" : ""
                      }`}
                      onClick={() =>
                        setForm((s) => ({
                          ...s,
                          brand_content_toggle: !s.brand_content_toggle,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="fb-toggle-row mt-4">
                  <span>Scheduling</span>
                  <button type="button" className="fb-switch" disabled />
                </div>
                <div className="gmb-char-count">
                  TikTok's API has no scheduling — use "Send to Drafts" to
                  finish and time the post inside the app.
                </div>
              </div>

              {/* ---------- MIDDLE: editor ---------- */}
              <div className="fb-composer-col">
                <div className="fb-composer-title">Create Post</div>
                <div className="fb-composer-sub">
                  {account
                    ? `@${account.username} selected`
                    : "No account selected"}
                </div>

                <div className="fb-channel-tabs">
                  {CHANNEL_TABS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      disabled={!c.ready}
                      className={channel === c.key ? "active" : ""}
                      onClick={() => c.ready && setChannel(c.key)}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                <div className="fb-account-row">
                  <Icon
                    icon="ic:baseline-tiktok"
                    style={{ color: "var(--tt-ink)" }}
                  />
                  TikTok Account:
                  <span className="fb-account-chip">
                    {account ? `@${account.username}` : "Not connected"}
                  </span>
                </div>

                {/* Audit status banner */}
                {creatorInfo && !canPostPublic && (
                  <div className="tt-notice warn">
                    <Icon
                      icon="mdi:shield-alert-outline"
                      className="tt-notice-icon"
                    />
                    <span>
                      Your TikTok app hasn't completed audit, so posts can only
                      be published privately (Only Me). Submit the app for
                      review in the TikTok developer portal to publish publicly,
                      or use “Send to Drafts” and post from the TikTok app.
                    </span>
                  </div>
                )}

                {creatorInfo?.maxVideoPostDurationSec && !isPhoto && (
                  <div className="tt-notice info">
                    <Icon icon="mdi:timer-outline" className="tt-notice-icon" />
                    <span>
                      This account can post videos up to{" "}
                      <b>{creatorInfo.maxVideoPostDurationSec} seconds</b> long.
                    </span>
                  </div>
                )}

                <div className="fb-field-title">Select Post Type</div>
                <div className="fb-pills">
                  {POST_TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={`fb-pill ${
                        form.post_type === t.key ? "active" : ""
                      }`}
                      onClick={() => {
                        setForm((s) => ({ ...s, post_type: t.key }));
                        setPublishState(null);
                      }}
                    >
                      {form.post_type === t.key ? (
                        <Icon icon="mdi:check" />
                      ) : (
                        <Icon icon={t.icon} />
                      )}
                      {t.label}
                    </button>
                  ))}
                </div>

                {isDraft && (
                  <div className="tt-notice info">
                    <Icon
                      icon="mdi:information-outline"
                      className="tt-notice-icon"
                    />
                    <span>
                      The video will land in your TikTok drafts. Captions,
                      sounds, effects and privacy are set inside the app when
                      you publish it.
                    </span>
                  </div>
                )}

                {/* ---- Photo carousel ---- */}
                {isPhoto ? (
                  <>
                    <div className="fb-field-title">
                      Add Images ( 1 - 35, vertical 1080 x 1920 recommended )
                    </div>
                    <div className="tt-photo-list">
                      {photos.map((p, i) => (
                        <div
                          className={`tt-photo-item ${
                            coverIndex === i ? "is-cover" : ""
                          }`}
                          key={p.url}
                          onClick={() => setCoverIndex(i)}
                        >
                          <img src={p.preview} alt="" />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPhotos((s) => s.filter((_, x) => x !== i));
                              setCoverIndex(0);
                            }}
                          >
                            <Icon icon="mdi:close" />
                          </button>
                          {coverIndex === i && (
                            <span className="tt-photo-cover-tag">Cover</span>
                          )}
                        </div>
                      ))}
                      {photos.length < 35 && (
                        <label className="tt-photo-add">
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => onPickPhoto(e.target.files[0])}
                          />
                          <Icon icon="mdi:plus" />
                        </label>
                      )}
                    </div>
                    <div className="gmb-char-count mt-2">
                      {photos.length} of 35 images · tap an image to set it as
                      the cover
                    </div>

                    <div className="fb-field-title mt-4">Title</div>
                    <input
                      className="form-control"
                      placeholder="Eg: 5 dishes you have to try"
                      maxLength={90}
                      value={form.title}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, title: e.target.value }))
                      }
                    />
                    <div className="gmb-char-count">
                      {form.title.length} / 90 characters
                    </div>
                  </>
                ) : (
                  <>
                    <div className="fb-field-title">
                      Add Video ( 9:16 vertical, 1080 x 1920 recommended )
                    </div>

                    {videoPreview ? (
                      <div className="fb-drop-preview">
                        <video src={videoPreview} controls />
                        <button
                          type="button"
                          className="fb-drop-remove"
                          onClick={() => {
                            setVideoFile(null);
                            setVideoPreview("");
                          }}
                        >
                          <Icon icon="mdi:close" />
                        </button>
                      </div>
                    ) : (
                      <label className="fb-dropzone">
                        <input
                          type="file"
                          accept="video/mp4,video/quicktime,video/webm"
                          onChange={(e) => onPickVideo(e.target.files[0])}
                        />
                        <div className="fb-drop-icon">
                          <Icon icon="mdi:video-plus-outline" />
                        </div>
                        <div className="fb-drop-title">
                          Upload or Drag Video
                        </div>
                        <div className="fb-drop-hint">
                          Maximum Size - 500 MB. Format MP4, MOV, WEBM
                        </div>
                      </label>
                    )}
                  </>
                )}

                {!isDraft && (
                  <>
                    <div className="fb-field-title mt-4">
                      {isPhoto ? "Description" : "Caption"}
                    </div>
                    <textarea
                      className="form-control"
                      rows={4}
                      placeholder="Eg: The one trick nobody tells you about #smallbusiness"
                      maxLength={isPhoto ? 4000 : 2200}
                      value={form.description}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, description: e.target.value }))
                      }
                    />
                    <div className="gmb-char-count">
                      {form.description.length} / {isPhoto ? 4000 : 2200}{" "}
                      characters
                    </div>
                  </>
                )}

                {/* ---- Publish progress ---- */}
                {publishState && (
                  <div
                    className={`tt-progress ${
                      publishState.isComplete
                        ? "done"
                        : publishState.isFailed
                          ? "failed"
                          : ""
                    }`}
                  >
                    <div className="tt-progress-head">
                      {publishState.isComplete ? (
                        <Icon icon="mdi:check-circle" />
                      ) : publishState.isFailed ? (
                        <Icon icon="mdi:alert-circle" />
                      ) : (
                        <span className="tt-progress-spinner" />
                      )}
                      {publishState.isComplete
                        ? "Done"
                        : publishState.isFailed
                          ? "Publish failed"
                          : "Publishing"}
                    </div>

                    {!publishState.isComplete && !publishState.isFailed && (
                      <div className="tt-progress-track">
                        <span
                          className="tt-progress-fill"
                          style={{
                            width:
                              publishState.status === "PROCESSING_UPLOAD"
                                ? "45%"
                                : publishState.status === "PROCESSING_DOWNLOAD"
                                  ? "75%"
                                  : "25%",
                          }}
                        />
                      </div>
                    )}

                    <div className="tt-progress-note">
                      {publishState.message}
                    </div>
                  </div>
                )}

                <div className="fb-submit-bar">
                  <button
                    type="button"
                    className="btn btn-outline-primary-600"
                    onClick={() => setShowComposer(false)}
                  >
                    {publishState?.isComplete ? "Close" : "Cancel"}
                  </button>
                  <button
                    type="button"
                    className="fb-submit"
                    disabled={
                      !account || (publishState && !publishState.isFailed)
                    }
                    onClick={publish}
                  >
                    <Icon icon="ic:baseline-tiktok" />
                    {isDraft ? "Send To TikTok Drafts" : "Publish To TikTok"}
                  </button>
                </div>
              </div>

              {/* ---------- RIGHT: phone preview + tips ---------- */}
              <div className="fb-composer-col fb-composer-aside">
                <div className="fb-preview">
                  <div className="fb-preview-head">
                    <Icon
                      icon="ic:baseline-tiktok"
                      style={{ color: "var(--tt-ink)" }}
                    />
                    TikTok Preview
                  </div>

                  <div style={{ padding: 14 }}>
                    <div className="tt-phone">
                      {isPhoto && photos[coverIndex] ? (
                        <img src={photos[coverIndex].preview} alt="" />
                      ) : videoPreview ? (
                        <video src={videoPreview} muted loop />
                      ) : (
                        <div className="tt-phone-empty">
                          <Icon icon="mdi:cellphone" style={{ fontSize: 34 }} />
                          No Media Added
                        </div>
                      )}

                      {(videoPreview || photos.length > 0) && (
                        <>
                          <div className="tt-phone-caption">
                            <b>@{account?.username || "yourbrand"}</b>
                            {isPhoto
                              ? form.title ||
                                form.description ||
                                "Your caption here"
                              : isDraft
                                ? "Caption added in the TikTok app"
                                : form.description || "Your caption here"}
                          </div>

                          <div className="tt-phone-rail">
                            <span>
                              <Icon icon="mdi:heart" />
                              Like
                            </span>
                            <span>
                              <Icon icon="mdi:comment" />
                              {form.disable_comment ? "Off" : "Comment"}
                            </span>
                            <span>
                              <Icon icon="mdi:share" />
                              Share
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="fb-composer-heading">Tips to Improve Reach</div>
                {TIPS.map((t) => (
                  <div className="fb-tip-card" key={t.title}>
                    <div className="fb-tip-icon">
                      <Icon icon="mdi:lightbulb-on-outline" />
                    </div>
                    <h6>{t.title}</h6>
                    <p>{t.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* ==================== VIDEO DETAIL ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showDetail}
          onHide={() => setShowDetail(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Video Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detailVideo && (
              <>
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <div className="tt-phone" style={{ maxHeight: 300 }}>
                      <img src={detailVideo.coverImage} alt="" />
                      <span className="tt-grid-duration">
                        {fmtDuration(detailVideo.duration)}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <p className="gmb-review-comment mt-0">
                      {detailVideo.title ||
                        detailVideo.description ||
                        "(no caption)"}
                    </p>
                    <div className="gmb-metric-range mb-3">
                      <span>{fmtDate(detailVideo.createdAt)}</span>
                      {detailVideo.shareUrl && (
                        <a
                          className="gmb-competitor-maps-link"
                          href={detailVideo.shareUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View on TikTok
                        </a>
                      )}
                    </div>

                    <div className="row g-2">
                      {[
                        ["Views", detailVideo.viewCount],
                        ["Likes", detailVideo.likeCount],
                        ["Comments", detailVideo.commentCount],
                        ["Shares", detailVideo.shareCount],
                      ].map(([label, value]) => (
                        <div className="col-6" key={label}>
                          <div className="gmb-metric-label">{label}</div>
                          <div className="gmb-mono" style={{ fontWeight: 800 }}>
                            {compact(value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="gmb-section-title mb-2">Comments</div>

                {commentsBlocked ? (
                  <div className="tt-notice warn">
                    <Icon
                      icon="mdi:shield-alert-outline"
                      className="tt-notice-icon"
                    />
                    <span>
                      Comment management needs TikTok's comment permissions,
                      which are granted once your app passes review. Until then,
                      comments can be managed in the TikTok app.
                    </span>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="gmb-empty">No comments yet.</div>
                ) : (
                  comments.map((c) => (
                    <div className="fb-comment" key={c.commentId}>
                      <div className="fb-comment-avatar">
                        {initials(c.username)}
                      </div>
                      <div className="fb-comment-body">
                        <b>@{c.username || "TikTok user"}</b>
                        <p>{c.text}</p>
                        <div className="fb-comment-actions">
                          <button
                            onClick={() =>
                              setReplyBox((s) => ({
                                ...s,
                                [c.commentId]: s[c.commentId] || "",
                              }))
                            }
                          >
                            Reply
                          </button>
                          <button onClick={() => toggleHide(c)}>
                            {c.status === "hidden" ? "Unhide" : "Hide"}
                          </button>
                          {c.isOwn && (
                            <button
                              className="danger"
                              onClick={() => removeComment(c.commentId)}
                            >
                              Delete
                            </button>
                          )}
                          <span style={{ color: "var(--gmb-ink-3)" }}>
                            <Icon icon="mdi:heart-outline" /> {c.likeCount}
                          </span>
                        </div>
                        {replyBox[c.commentId] !== undefined && (
                          <div className="d-flex gap-2 mt-2">
                            <input
                              className="form-control"
                              placeholder="Write a reply..."
                              maxLength={150}
                              value={replyBox[c.commentId]}
                              onChange={(e) =>
                                setReplyBox((s) => ({
                                  ...s,
                                  [c.commentId]: e.target.value,
                                }))
                              }
                            />
                            <button
                              className="btn btn-primary-600 btn-sm"
                              onClick={() => sendReply(c.commentId)}
                            >
                              Send
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </MasterLayout>
  );
}

export default TikTokPanel;
